import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BackToTop from '../../components/BackToTop/BackToTop';
import LogoSlider from '../../components/LogoSlider/LogoSlider';
import './Home.css';

const Home = () => {
  const [counters, setCounters] = useState({
    years: 0,
    partners: 0,
    turnover: 0,
    subsidiaries: 0,
    divisions: 0
  });

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      const targets = {
        years: 48,
        partners: 100,
        turnover: 18.67,
        subsidiaries: 11,
        divisions: 3
      };

      let currentStep = 0;

      const counterInterval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          years: Math.floor(progress * targets.years),
          partners: Math.floor(progress * targets.partners),
          turnover: parseFloat((progress * targets.turnover).toFixed(2)),
          subsidiaries: Math.floor(progress * targets.subsidiaries),
          divisions: Math.floor(progress * targets.divisions)
        });

        if (currentStep >= steps) {
          clearInterval(counterInterval);
          setCounters(targets);
        }
      }, stepDuration);

      return () => clearInterval(counterInterval);
    };

    // Start counter animation when component mounts
    animateCounters();
  }, []);

  const partnerLogos = [
    { src: "/partners logo/Chemdo.png", alt: "Chemdo" },
    { src: "/partners logo/Formosa Plastics.png", alt: "Formosa Plastics" },
    { src: "/partners logo/Billion PET.png", alt: "Billion PET" },
    { src: "/partners logo/China Resources.png", alt: "China Resources" },
    { src: "/partners logo/EastLake PVC.png", alt: "EastLake PVC" },
    { src: "/partners logo/JADE PET Resin.png", alt: "JADE PET Resin" },
    { src: "/partners logo/GAP Polymers.png", alt: "GAP Polymers" },
    { src: "/partners logo/Wankai.png", alt: "Wankai" },
    { src: "/partners logo/Tricon.png", alt: "Tricon" },
    { src: "/partners logo/Ruiqi Chemical.png", alt: "Ruiqi Chemical" },
    { src: "/partners logo/Hyosung Chemical.png", alt: "Hyosung Chemical" },
    { src: "/partners logo/SCG Chemicals.png", alt: "SCG Chemicals" },
    { src: "/partners logo/Innoplus.png", alt: "Innoplus" },
    { src: "/partners logo/Hengli Petrochemical.png", alt: "Hengli Petrochemical" },
    { src: "/partners logo/Jiayue Group.png", alt: "Jiayue Group" },
    { src: "/partners logo/Haiwan.png", alt: "Haiwan" },
    { src: "/partners logo/OQ.png", alt: "OQ" },
    { src: "/partners logo/SM Korea Shipping.png", alt: "SM Korea Shipping" }
  ];

  const divisionLogos = [
    { src: "/slider/Trimline Group.png", alt: "Trimline Group" },
    { src: "/slider/Trimline Polymers.png", alt: "Trimline Polymers" },
    { src: "/slider/Trimline Commodities.png", alt: "Trimline Commodities" },
    { src: "/slider/Trimline Chemicals.png", alt: "Trimline Chemicals" },
    { src: "/slider/Trimline Metals.png", alt: "Trimline Metals" },
    { src: "/slider/Trimline Techventures.png", alt: "Trimline Techventures" },
    { src: "/slider/Khazanay.png", alt: "Khazanay" },
    { src: "/slider/Fadano.png", alt: "Fadano" },
    { src: "/slider/Birdsoles.png", alt: "Birdsoles" },
    { src: "/slider/Ronmers Coffee.png", alt: "Ronmers Coffee" },
    { src: "/slider/Powerpresent ai.png", alt: "Powerpresent ai" },
    { src: "/slider/yaara ai.png", alt: "Yaara ai" },
    { src: "/slider/sawda ai.png", alt: "sawda ai" }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <video className="hero-video" autoPlay muted loop playsInline>
            <source src="/video/video1.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">TRIMLINE GROUP</h1>
            <p className="hero-subtitle">CONGLOMERATE FOCUSED ON INTERNATIONAL TRADE, E-COMMERCE & TECHNOLOGY</p>
            <div className="hero-buttons">
              <Link to="/about" className="btn">Our Story</Link>
              <Link to="/contact" className="btn btn-outline">Be a Partner</Link>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">{counters.years}</span>
                <span className="stat-label">Years of Excellence</span>
              </div>
              <div className="stat">
                <span className="stat-number">{counters.partners}+</span>
                <span className="stat-label">Partners Worldwide</span>
              </div>
              <div className="stat">
                <span className="stat-number">${counters.turnover}M</span>
                <span className="stat-label">Yearly Turnover</span>
              </div>
              <div className="stat">
                <span className="stat-number">{counters.subsidiaries}</span>
                <span className="stat-label">Subsidiaries</span>
              </div>
              <div className="stat">
                <span className="stat-number">{counters.divisions}</span>
                <span className="stat-label">Divisions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="section-title">
            <h2>Our Core Business Divisions</h2>
            <p>Trimline Group delivers excellence across multiple industries through specialized subsidiaries</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-globe-americas"></i>
              </div>
              <h3>International Trade</h3>
              <p>Global supply chain solutions across polymers, chemicals, and commodities with operations in USA, UAE, China, and Singapore.</p>
              <Link to="/trade" className="btn">Explore <i className="fas fa-arrow-right"></i></Link>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-shopping-cart"></i>
              </div>
              <h3>E-Commerce & Brands</h3>
              <p>Leading Pakistan's digital retail market with imported footwear brands Khazanay.pk, Fadano, Birdsoles and a premium portfolio of FMCG products.</p>
              <Link to="/brands" className="btn">Explore <i className="fas fa-arrow-right"></i></Link>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-microchip"></i>
              </div>
              <h3>Technology</h3>
              <p>Innovative AI solutions with PowerPresent.ai, Yaara.ai, and Sawda.ai driving digital transformation.</p>
              <Link to="/tech" className="btn">Explore <i className="fas fa-arrow-right"></i></Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Nearly Five Decades of Excellence</h2>
              <p>Since 1977, Trimline Group has established itself as a pioneer in global supply chain solutions and diversified business operations. With a growing focus on e-commerce and digital consumer brands, we continue to innovate while maintaining our legacy of excellence.</p>
              <div className="about-highlights">
                <div className="highlight">
                  <i className="fas fa-check-circle"></i>
                  <span>Transparent business operations</span>
                </div>
                <div className="highlight">
                  <i className="fas fa-check-circle"></i>
                  <span>Global network of partners</span>
                </div>
                <div className="highlight">
                  <i className="fas fa-check-circle"></i>
                  <span>Innovation-driven approach</span>
                </div>
              </div>
              <Link to="/about" className="btn">More about us</Link>
            </div>
            <div className="about-visual">
              <div className="timeline-visual">
                <div className="timeline-milestone">
                  <div className="milestone-year">1977</div>
                  <div className="milestone-dot"></div>
                  <div className="milestone-text">Founded</div>
                </div>
                <div className="timeline-milestone">
                  <div className="milestone-year">1990s</div>
                  <div className="milestone-dot"></div>
                  <div className="milestone-text">Global Expansion</div>
                </div>
                <div className="timeline-milestone">
                  <div className="milestone-year">2000s</div>
                  <div className="milestone-dot"></div>
                  <div className="milestone-text">Diversification</div>
                </div>
                <div className="timeline-milestone">
                  <div className="milestone-year">Today</div>
                  <div className="milestone-dot"></div>
                  <div className="milestone-text">Innovation Leader</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="global-section">
        <div className="container">
          <div className="section-title">
            <h2>Global Presence</h2>
            <p>Strategically located operations across key economic hubs worldwide</p>
          </div>
          <div className="global-map">
            <div className="map-container">
              <div className="world-map">
                <img src="/logos/Map.jpg" alt="World Map" className="world-map-img" />
                <div className="location-point usa" data-country="USA">
                  <div className="point-pulse"></div>
                  <div className="point-info">
                    <h4>USA</h4>
                    <p>Trimline International LLC</p>
                  </div>
                </div>
                <div className="location-point uae" data-country="UAE">
                  <div className="point-pulse"></div>
                  <div className="point-info">
                    <h4>UAE</h4>
                    <p>Trimline International LLC</p>
                  </div>
                </div>
                <div className="location-point china" data-country="China">
                  <div className="point-pulse"></div>
                  <div className="point-info">
                    <h4>China</h4>
                    <p>Jiangyin Jietong Zhejiang Chemicals</p>
                  </div>
                </div>
                <div className="location-point singapore" data-country="Singapore">
                  <div className="point-pulse"></div>
                  <div className="point-info">
                    <h4>Singapore</h4>
                    <p>Intersmart Singapore</p>
                  </div>
                </div>
                <div className="location-point pakistan" data-country="Pakistan">
                  <div className="point-pulse"></div>
                  <div className="point-info">
                    <h4>Pakistan</h4>
                    <p>E-commerce Operations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Logo Slider */}
      <LogoSlider 
        title="Our Business Partners"
        description="Proudly working with industry leaders worldwide."
        logos={partnerLogos}
      />

      {/* Subsidiaries Preview */}
      <section className="subsidiaries-preview">
        <div className="container">
          <div className="section-title">
            <h2>Our Subsidiaries</h2>
            <p>Diverse business units working together under the Trimline Group umbrella</p>
          </div>
          <div className="subsidiaries-grid">
            <div className="subsidiary-card">
              <div className="subsidiary-icon">
                <i className="fas fa-industry"></i>
              </div>
              <h3>Trimline Polymers</h3>
              <p>Specialized in supply of industrial polymers and raw materials for packaging and manufacturing industries.</p>
            </div>
            <div className="subsidiary-card">
              <div className="subsidiary-icon">
                <i className="fas fa-leaf"></i>
              </div>
              <h3>Trimline Jade Chemicals</h3>
              <p>Suppliers of premium chemicals for industrial and commercial applications worldwide.</p>
            </div>
            <div className="subsidiary-card">
              <div className="subsidiary-icon">
                <i className="fas fa-laptop-code"></i>
              </div>
              <h3>Trimline Tech Ventures</h3>
              <p>Dedicated to technology solutions, innovation, and global digital transformation projects.</p>
            </div>
          </div>
          <div className="text-center">
            <Link to="/subsidiaries" className="btn">View All</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to explore opportunities with us?</h2>
            <p>Get in touch with our team to discuss how we can collaborate and create value together.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn">Contact Us</Link>
              <Link to="/about" className="btn btn-outline">Learn More</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions Logo Slider */}
      <LogoSlider 
        title="Divisions & Brands"
        description="Trusted by industry leaders worldwide"
        logos={divisionLogos}
      />

      <BackToTop />
    </div>
  );
};

export default Home;